import { apiClient } from '../utils/apiClient';
import type {
    Position,
    CreatePositionRequest,
    UpdatePositionRequest,
} from '@arrowise/shared';

// Response shapes as returned by the backend routes
interface PositionsResponse { positions: Position[] }
interface PositionResponse { position: Position }
interface DeletePositionResponse { deleted: boolean; sessionsDeleted: number }

class PositionService {
    // List positions for user or team
    async listPositions(teamId?: string): Promise<Position[]> {
        const params = teamId ? { teamId } : undefined;
        const response = await apiClient.get<PositionsResponse>('/positions', params);
        if (!response.success || !response.data?.positions) {
            throw new Error(response.message || 'Failed to fetch positions');
        }
        return response.data.positions;
    }

    // Get a single position by ID
    async getPositionById(positionId: string): Promise<Position> {
        const response = await apiClient.get<PositionResponse>(`/positions/${positionId}`);
        if (!response.success || !response.data?.position) {
            throw new Error(response.message || 'Failed to fetch position');
        }
        return response.data.position;
    }

    // Create a new position
    async createPosition(data: CreatePositionRequest): Promise<Position> {
        const response = await apiClient.post<PositionResponse>('/positions', data);
        if (!response.success || !response.data?.position) {
            throw new Error(response.message || 'Failed to create position');
        }
        return response.data.position;
    }

    // Update an existing position
    async updatePosition(positionId: string, data: UpdatePositionRequest): Promise<Position> {
        const response = await apiClient.put<PositionResponse>(`/positions/${positionId}`, data);
        if (!response.success || !response.data?.position) {
            throw new Error(response.message || 'Failed to update position');
        }
        return response.data.position;
    }

    // Delete a position
    async deletePosition(positionId: string): Promise<{ deleted: boolean; sessionsDeleted: number }> {
        const response = await apiClient.delete<DeletePositionResponse>(`/positions/${positionId}`);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'Failed to delete position');
        }
        return response.data;
    }
}

export const positionService = new PositionService();
export default positionService;
